/**
 * Shared badge colour language — one source of truth for severity / status
 * chip tones across the feature pages. Previously each page declared its own
 * SEV_BADGE map (ExploitationRace, AIVulnDiscovery, PurpleRange…) and they
 * drifted: some used cyan for "medium", some amber; some used different rose
 * shades. This unifies them.
 *
 * Colour semantics (consistent everywhere):
 *   rose    = bad / urgent / critical
 *   amber   = warning / high / caution
 *   cyan    = medium / informational-active
 *   emerald = good / low / on-target
 *   blue    = neutral info
 *
 * Tokens (bg/text/border) all come from the Tailwind design palette
 * (rose/amber/cyan/emerald/blue + *-light). No raw palette colours.
 */

/** Severity → chip classes. Canonical map used by SeverityBadge + pages. */
export const SEV_BADGE: Record<string, string> = {
  critical: 'bg-rose/10 text-rose-light border-rose/30',
  high:     'bg-amber/10 text-amber-light border-amber/30',
  medium:   'bg-cyan/10 text-cyan border-cyan/30',
  low:      'bg-emerald/10 text-emerald border-emerald/30',
  info:     'bg-blue/10 text-blue-light border-blue/30',
}

/** Residual-risk → chip classes. critical/high both read as urgent (rose). */
export const RESIDUAL_BADGE: Record<string, string> = {
  critical: 'bg-rose/10 text-rose-light border-rose/30',
  high:     'bg-rose/10 text-rose-light border-rose/30',
  medium:   'bg-amber/10 text-amber-light border-amber/30',
  low:      'bg-emerald/10 text-emerald border-emerald/30',
}

/** Fallback used when a severity key is unknown. */
export const SEV_BADGE_FALLBACK = SEV_BADGE.medium

/** Look up a severity tone, case-insensitively, with a safe fallback. */
export function sevBadge(sev: string | null | undefined): string {
  return SEV_BADGE[(sev || '').toLowerCase()] || SEV_BADGE_FALLBACK
}
